import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import FlightLandIcon from '@material-ui/icons/FlightLand';
import HotelIcon from '@material-ui/icons/Hotel';
import PeopleIcon from '@material-ui/icons/People';
import WorkIcon from '@material-ui/icons/Work';
import StarIcon from '@material-ui/icons/Star';
import EventIcon from '@material-ui/icons/Event';
import RestaurantIcon from '@material-ui/icons/Restaurant';
import KingBedIcon from '@material-ui/icons/KingBed';

/**
 * BookingDetails - Unified component for displaying booking summary
 * Used by both BookingSidebar (desktop) and BookingDetailsModal (mobile)
 */

// Helper function to get day abbreviation key from day number (0=Sunday, 6=Saturday)
const getDayKey = (dayNumber) => {
  const days = ['sundayShort', 'mondayShort', 'tuesdayShort', 'wednesdayShort', 'thursdayShort', 'fridayShort', 'saturdayShort'];
  return days[dayNumber];
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return moment.utc(dateStr).format('HH:mm');
};

const BookingDetails = ({
  packageDetails,
  quoteDetails,
  totalPrice,
  basePrice,
  baggagePrice = 0,
  paymentsCount = 1,
  currentStep = 1,
  passengers = [],
  baggageState = [],
  currency = 'USD',
  variant = 'sidebar',
  showPrice = true
}) => {
  const { t } = useTranslation();
  const data = packageDetails?.data;
  const currencySymbol = currency === 'USD' ? '$' : currency;

  // Date formatting functions using translation
  const formatDateWithHebrewDay = (dateStr) => {
    if (!dateStr) return '';
    const date = moment.utc(dateStr);
    const dayKey = getDayKey(date.day());
    const dayLetter = t(`common.days.${dayKey}`);
    return `${dayLetter} ${date.format('DD/MM/YYYY')}`;
  };

  const formatShortDateWithDay = (dateStr) => {
    if (!dateStr) return '';
    const date = moment.utc(dateStr);
    const dayKey = getDayKey(date.day());
    const dayLetter = t(`common.days.${dayKey}`);
    return `${dayLetter} ${date.format('DD/MM')}`;
  };

  // Get meal plan name from translation
  const getMealPlanName = (code) => {
    if (!code) return null;
    switch (code) {
      case 'HM': return t('hotel.mealPlans.halfBoard');
      case 'MB': return t('hotel.mealPlans.halfBoardWithDrinks');
      case 'MP': return t('hotel.mealPlans.halfBoard');
      case 'PC': return t('hotel.mealPlans.fullBoard');
      case 'PV': return t('hotel.mealPlans.fullBoardWithDrinks');
      case 'TI': return t('hotel.mealPlans.allInclusive');
      case 'TP': return t('hotel.mealPlans.allInclusivePremium');
      case 'HD': return t('hotel.mealPlans.breakfast');
      default: return null;
    }
  };

  // Destination
  const destination = data?.destination?.city?.name || quoteDetails?.destination?.city_name;
  const country = data?.destination?.country?.name || '';

  // Dates - from departure/return objects
  const departureDate = data?.departure?.date;
  const returnDate = data?.return?.date;
  const nights = data?.nights;

  // Flight details - API uses flat structure with _iata, _date suffixes
  const outboundFlight = data?.flight?.outbound;
  const inboundFlight = data?.flight?.inbound;

  // Hotel details
  const hotel = data?.property;
  const hotelName = hotel?.name;
  const hotelStars = hotel?.stars;
  const mealPlan = hotel?.meal_code;
  const roomName = hotel?.room?.name;

  // Passenger counts from pax object or quoteDetails
  const pax = data?.pax;
  const adultsCount = pax?.adults || (
    (quoteDetails?.room1_adults_count || 0) +
    (quoteDetails?.room2_adults_count || 0) +
    (quoteDetails?.room3_adults_count || 0) +
    (quoteDetails?.room4_adults_count || 0)
  );
  const childrenCount = pax?.children || (
    (quoteDetails?.room1_children_ages?.length || 0) +
    (quoteDetails?.room2_children_ages?.length || 0) +
    (quoteDetails?.room3_children_ages?.length || 0) +
    (quoteDetails?.room4_children_ages?.length || 0)
  );
  const totalPassengers = adultsCount + childrenCount;

  // Baggage summary
  const paidBaggageItems = baggageState.filter(b => b.baggage?.price > 0);
  const paidBaggageCount = paidBaggageItems.length;

  // Filtered passengers with names
  const namedPassengers = passengers.filter(p => p?.first_name);

  const isModal = variant === 'modal';
  const prefix = isModal ? 'modal' : 'sidebar';

  return (
    <div className={`booking-details booking-details--${variant}`} role="complementary" aria-label={t('reservation.wizard.bookingDetails.ariaLabel')}>
      {/* Destination & Dates Header */}
      <div className={`${prefix}-destination-block`} role="group" aria-label={t('reservation.wizard.bookingDetails.destinationAndDates')}>
        {departureDate && returnDate && (
          <div className={`${prefix}-dates-row`}>
            <EventIcon className={`${prefix}-icon-small`} aria-hidden="true" />
            <span className={`${prefix}-dates-text`}>
              {formatShortDateWithDay(departureDate)} - {formatShortDateWithDay(returnDate)}
            </span>
            {nights && <span className={`${prefix}-nights-badge`} aria-label={t('reservation.wizard.bookingDetails.nightsLabel', { count: nights })}>{t('common.labels.nights', { count: nights })}</span>}
          </div>
        )}

        {totalPassengers > 0 && (
          <div className={`${prefix}-pax-row`}>
            <PeopleIcon className={`${prefix}-icon-small`} aria-hidden="true" />
            <span aria-label={t('reservation.wizard.bookingDetails.passengersLabel', { adults: adultsCount, children: childrenCount > 0 ? t('reservation.wizard.bookingDetails.passengersWithChildren', { count: childrenCount }) : '' })}>
              {adultsCount > 0 && `${adultsCount} ${t('common.labels.adults')}`}
              {childrenCount > 0 && ` + ${childrenCount} ${t('common.labels.children')}`}
            </span>
          </div>
        )}
      </div>

      {/* Flights Section */}
      <div className={`${prefix}-section`} role="group" aria-label={t('reservation.steps.orderSummary.flightDetails')}>
        <div className={`${prefix}-section-title`}>{t('reservation.wizard.bookingDetails.flights.title')}</div>

        {/* Outbound Flight - using API structure: departure_iata, departure_date, etc. */}
        {outboundFlight && (
          <div className={`${prefix}-flight-card`} role="group" aria-label={t('reservation.wizard.bookingDetails.flights.outbound')}>
            <div className={`${prefix}-flight-header`}>
              <FlightTakeoffIcon className={`${prefix}-flight-icon`} aria-hidden="true" />
              <span className={`${prefix}-flight-label`}>{t('reservation.wizard.bookingDetails.flights.outbound')}</span>
            </div>
            <div className={`${prefix}-flight-route`} aria-label={t('reservation.wizard.bookingDetails.flights.route', { from: outboundFlight.departure_iata_name || outboundFlight.departure_iata, to: outboundFlight.arrival_iata_name || outboundFlight.arrival_iata })}>
              {outboundFlight.airline?.code && (
                <span className={`${prefix}-airline-inline`}>
                  <img
                    src={outboundFlight.airline.icon || `/images/airlines/32/${outboundFlight.airline.code}.png`}
                    alt={t('reservation.wizard.bookingDetails.flights.airlineAlt', { name: outboundFlight.airline.name || outboundFlight.airline.code })}
                    className={`${prefix}-airline-icon`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </span>
              )}
              <span className={`${prefix}-airport`}>
                {outboundFlight.departure_iata_name || outboundFlight.departure_iata}
                {outboundFlight.departure_iata_name && <span className={`${prefix}-airport-code`}> ({outboundFlight.departure_iata})</span>}
              </span>
              <span className={`${prefix}-arrow`} aria-hidden="true">←</span>
              <span className={`${prefix}-airport`}>
                {outboundFlight.arrival_iata_name || outboundFlight.arrival_iata}
                {outboundFlight.arrival_iata_name && <span className={`${prefix}-airport-code`}> ({outboundFlight.arrival_iata})</span>}
              </span>
            </div>
            <div className={`${prefix}-flight-times`}>
              <div className={`${prefix}-flight-time-row`}>
                <span className={`${prefix}-flight-time-label`}>{t('reservation.wizard.bookingDetails.flights.departure')}</span>
                <span className={`${prefix}-flight-time-value`}>
                  {formatDateWithHebrewDay(outboundFlight.departure_date)}
                  <span className={`${prefix}-flight-time`}>{formatTime(outboundFlight.departure_date)}</span>
                </span>
              </div>
              <div className={`${prefix}-flight-time-row`}>
                <span className={`${prefix}-flight-time-label`}>{t('reservation.wizard.bookingDetails.flights.arrival')}</span>
                <span className={`${prefix}-flight-time-value`}>
                  {formatDateWithHebrewDay(outboundFlight.arrival_date)}
                  <span className={`${prefix}-flight-time`}>{formatTime(outboundFlight.arrival_date)}</span>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Inbound Flight */}
        {inboundFlight && (
          <div className={`${prefix}-flight-card`} role="group" aria-label={t('reservation.wizard.bookingDetails.flights.inbound')}>
            <div className={`${prefix}-flight-header`}>
              <FlightLandIcon className={`${prefix}-flight-icon`} aria-hidden="true" />
              <span className={`${prefix}-flight-label`}>{t('reservation.wizard.bookingDetails.flights.inbound')}</span>
            </div>
            <div className={`${prefix}-flight-route`} aria-label={t('reservation.wizard.bookingDetails.flights.route', { from: inboundFlight.departure_iata_name || inboundFlight.departure_iata, to: inboundFlight.arrival_iata_name || inboundFlight.arrival_iata })}>
              {inboundFlight.airline?.code && (
                <span className={`${prefix}-airline-inline`}>
                  <img
                    src={inboundFlight.airline.icon || `/images/airlines/32/${inboundFlight.airline.code}.png`}
                    alt={t('reservation.wizard.bookingDetails.flights.airlineAlt', { name: inboundFlight.airline.name || inboundFlight.airline.code })}
                    className={`${prefix}-airline-icon`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </span>
              )}
              <span className={`${prefix}-airport`}>
                {inboundFlight.departure_iata_name || inboundFlight.departure_iata}
                {inboundFlight.departure_iata_name && <span className={`${prefix}-airport-code`}> ({inboundFlight.departure_iata})</span>}
              </span>
              <span className={`${prefix}-arrow`} aria-hidden="true">←</span>
              <span className={`${prefix}-airport`}>
                {inboundFlight.arrival_iata_name || inboundFlight.arrival_iata}
                {inboundFlight.arrival_iata_name && <span className={`${prefix}-airport-code`}> ({inboundFlight.arrival_iata})</span>}
              </span>
            </div>
            <div className={`${prefix}-flight-times`}>
              <div className={`${prefix}-flight-time-row`}>
                <span className={`${prefix}-flight-time-label`}>{t('reservation.wizard.bookingDetails.flights.departure')}</span>
                <span className={`${prefix}-flight-time-value`}>
                  {formatDateWithHebrewDay(inboundFlight.departure_date)}
                  <span className={`${prefix}-flight-time`}>{formatTime(inboundFlight.departure_date)}</span>
                </span>
              </div>
              <div className={`${prefix}-flight-time-row`}>
                <span className={`${prefix}-flight-time-label`}>{t('reservation.wizard.bookingDetails.flights.arrival')}</span>
                <span className={`${prefix}-flight-time-value`}>
                  {formatDateWithHebrewDay(inboundFlight.arrival_date)}
                  <span className={`${prefix}-flight-time`}>{formatTime(inboundFlight.arrival_date)}</span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hotel Section */}
      {hotelName && (
        <div className={`${prefix}-section`} role="group" aria-label={t('reservation.steps.orderSummary.hotelDetails')}>
          <div className={`${prefix}-section-title`}>
            <HotelIcon className={`${prefix}-section-icon`} aria-hidden="true" />
            {t('reservation.wizard.bookingDetails.hotel.title')}
          </div>
          <div className={`${prefix}-hotel-card`}>
            <div className={`${prefix}-hotel-name`}>{hotelName}</div>
            {hotelStars > 0 && (
              <div className={`${prefix}-stars`} aria-label={t('reservation.wizard.bookingDetails.hotel.starsLabel', { count: hotelStars })}>
                {Array(hotelStars).fill(0).map((_, i) => (
                  <StarIcon key={i} className={`${prefix}-star`} aria-hidden="true" />
                ))}
              </div>
            )}
            <div className={`${prefix}-hotel-details`}>
              {roomName && (
                <div className={`${prefix}-hotel-detail`}>
                  <KingBedIcon className={`${prefix}-detail-icon`} aria-hidden="true" />
                  <span aria-label={t('reservation.wizard.bookingDetails.hotel.roomType', { name: roomName })}>{roomName}</span>
                </div>
              )}
              {getMealPlanName(mealPlan) && (
                <div className={`${prefix}-hotel-detail ${prefix}-meal-plan`}>
                  <RestaurantIcon className={`${prefix}-detail-icon`} aria-hidden="true" />
                  <span aria-label={t('reservation.wizard.bookingDetails.hotel.mealPlan', { plan: getMealPlanName(mealPlan) })}>{getMealPlanName(mealPlan)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Passengers Section (shown after step 2) */}
      {currentStep > 2 && namedPassengers.length > 0 && (
        <div className={`${prefix}-section`} role="group" aria-label={t('reservation.wizard.bookingDetails.passengers.listLabel', { count: namedPassengers.length })}>
          <div className={`${prefix}-section-title`}>
            <PeopleIcon className={`${prefix}-section-icon`} aria-hidden="true" />
            {t('reservation.wizard.bookingDetails.passengers.title', { count: namedPassengers.length })}
          </div>
          <div className={`${prefix}-passengers-list`} role="list" aria-label={t('reservation.wizard.bookingDetails.passengers.listLabel', { count: namedPassengers.length })}>
            {namedPassengers.map((p, idx) => (
              <div key={idx} className={`${prefix}-passenger-name`} role="listitem">
                {p.first_name} {p.last_name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Baggage Section (shown after step 3) */}
      {1==2 && currentStep > 3 && paidBaggageCount > 0 && (
        <div className={`${prefix}-section`} role="group" aria-label={t('reservation.wizard.bookingDetails.baggage.title')}>
          <div className={`${prefix}-section-title`}>
            <WorkIcon className={`${prefix}-section-icon`} aria-hidden="true" />
            {t('reservation.wizard.bookingDetails.baggage.title')}
          </div>
          <div className={`${prefix}-baggage-summary`} aria-label={t('reservation.wizard.bookingDetails.baggage.summary', { count: paidBaggageCount, cost: baggagePrice > 0 ? t('reservation.wizard.bookingDetails.baggage.cost', { price: baggagePrice, currency }) : '' })}>
            {paidBaggageCount} {t('common.labels.passengers')}

          </div>
        </div>
      )}

      {/* Flex Booking Badge */}
      {1==2 && quoteDetails?.is_flex && (
        <div className={`${prefix}-flex-badge`} role="status" aria-label={`${t('reservation.wizard.bookingDetails.flex.title')} - ${t('reservation.wizard.bookingDetails.flex.subtitle')}`}>
          <div className={`${prefix}-flex-title`}>{t('reservation.wizard.bookingDetails.flex.title')}</div>
          <div className={`${prefix}-flex-subtitle`}>{t('reservation.wizard.bookingDetails.flex.subtitle')}</div>
        </div>
      )}

      {/* Price Section */}
      {1==2 && showPrice && (
        <div className={`${prefix}-price-section`} role="group" aria-label={t('reservation.wizard.bookingDetails.price.title')}>
          <div className={`${prefix}-price-label`}>{t('reservation.wizard.bookingDetails.price.total')}</div>
          <div className={`${prefix}-price-value`} aria-label={t('reservation.wizard.bookingDetails.price.totalLabel', { price: totalPrice?.toLocaleString(), currency })}>
            {currencySymbol}{totalPrice?.toLocaleString()}
          </div>
          {baggagePrice > 0 && (
            <div className={`${prefix}-price-breakdown`}>
              {t('reservation.wizard.bookingDetails.price.baggageIncluded', { symbol: currencySymbol, price: baggagePrice })}
            </div>
          )}
          {paymentsCount > 1 && (
            <div className={`${prefix}-payments-info`} aria-label={t('reservation.wizard.bookingDetails.price.paymentsInfoLabel', { count: paymentsCount })}>
              {t('reservation.wizard.bookingDetails.price.paymentsInfo', { count: paymentsCount })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingDetails;
